import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Leaf } from 'lucide-react';

const FOUNDER_PORTRAIT = 'https://customer-assets.emergentagent.com/job_b94b95c1-d586-44dc-829b-05989d33f0ec/artifacts/1odmxzgb_FOunders%20big.jpeg';
const ELEPHANT_IMG = 'https://customer-assets.emergentagent.com/job_b94b95c1-d586-44dc-829b-05989d33f0ec/artifacts/ctq49vyv_FOunders%20small.jpeg';

const FoundersSection = () => {
  return (
    <section id="founders" className="relative bg-[#0B0F0E] py-32 md:py-44 overflow-hidden" data-testid="founders-section">
      {/* ambient warmth */}
      <div className="pointer-events-none absolute -top-40 right-10 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/[0.04] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#9E7A36]/[0.05] blur-3xl" />

      <div className="max-w-[1480px] mx-auto px-6 md:px-12">

        {/* chapter heading */}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 lg:col-span-3">
            <div className="overline">Prologue</div>
            <div className="numeral text-7xl md:text-8xl text-[#D4AF37]/30 mt-4">00</div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-[#E8E3D9] leading-[1.05]"
            >
              Two paths met<br/>
              in the forest —<br/>
              <span className="italic text-[#D4AF37]">and never parted.</span>
            </motion.h2>
          </div>
        </div>

        {/* split layout: portrait + cinematic story */}
        <div className="grid grid-cols-12 gap-8 md:gap-14 items-stretch">

          {/* portrait column — film strip composition */}
          <div className="col-span-12 lg:col-span-5">
            <div className="relative">
              {/* main portrait placeholder */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                className="relative aspect-[4/5] overflow-hidden border border-[#D4AF37]/30 bg-[#1A2421]"
              >
                <img
                  src={FOUNDER_PORTRAIT}
                  alt="Dilip Kariyappa & Vaishnavi Suvarna"
                  className="w-full h-full object-cover"
                  data-testid="founders-portrait"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F0E] via-transparent to-transparent" />

                {/* film perforations on edges */}
                <div className="absolute left-0 inset-y-0 w-2 flex flex-col justify-around py-3">
                  {Array(10).fill(0).map((_, i) => (
                    <div key={i} className="w-1 h-2 bg-[#D4AF37]/40 mx-auto" />
                  ))}
                </div>
                <div className="absolute right-0 inset-y-0 w-2 flex flex-col justify-around py-3">
                  {Array(10).fill(0).map((_, i) => (
                    <div key={i} className="w-1 h-2 bg-[#D4AF37]/40 mx-auto" />
                  ))}
                </div>

                {/* placeholder caption */}
                <div className="absolute bottom-4 left-6 right-6">
                  <div className="overline mb-1">Founders</div>
                  <div className="font-serif text-xl text-[#E8E3D9] italic">
                    Dilip & Vaishnavi
                  </div>
                </div>
              </motion.div>

              {/* secondary elephant frame */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.4 }}
                className="absolute -bottom-12 -right-4 md:-right-12 w-32 md:w-44 aspect-square overflow-hidden border border-[#D4AF37]/40 bg-[#1A2421] shadow-2xl"
              >
                <img src={ELEPHANT_IMG} alt="Dilip & Vaishnavi in the misty forest" className="w-full h-full object-cover" />
              </motion.div>

              {/* film clapboard meta */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.6 }}
                className="hidden md:flex absolute -left-4 top-8 bg-[#0B0F0E] border border-[#D4AF37]/30 px-4 py-3 flex-col gap-1"
              >
                <div className="overline text-[8px]">Reel · 01</div>
                <div className="text-[#E8E3D9] font-serif italic text-sm">Wayanad · where it began</div>
              </motion.div>
            </div>

            <p className="text-[10px] tracking-[0.25em] uppercase text-[#8E9A95] mt-16 italic">
              Dilip Kariyappa & Vaishnavi Suvarna · Founders
            </p>
          </div>

          {/* cinematic narrative */}
          <div className="col-span-12 lg:col-span-7 lg:pl-6">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              className="space-y-7 text-[#C4BFB1] text-[15px] md:text-[16px] leading-[1.95] font-light"
            >
              <p className="font-serif italic text-2xl md:text-3xl text-[#D4AF37] leading-tight not-italic-paragraph">
                <span className="text-[#D4AF37] italic">"Two travellers, one forest — and a story Wayanad still remembers."</span>
              </p>

              <p>
                Years ago, in the green folds of <span className="text-[#E8E3D9]">Wayanad</span>, a young
                <span className="text-[#E8E3D9]"> Dilip Kariyappa</span> worked as a tour guide — leading
                visitors deep into the forests of the Western Ghats, sharing the land he knew by heart.
              </p>

              <p>
                It was on one such trip that <span className="text-[#E8E3D9]">Vaishnavi Suvarna</span> arrived,
                a traveller drawn to the wild. Their paths crossed here, and over those forest days a quiet
                bond grew between them.
              </p>

              <div className="brass-rule w-24 my-2" />

              <p>
                A few years later, the two were married. And not long after, together they built a place of
                their own — <span className="text-[#D4AF37]">Tuskermark Safari Resort</span> — a way to share
                the forest that first brought them together, with anyone who comes asking softly.
              </p>

              <p className="font-serif italic text-xl md:text-2xl text-[#E8E3D9] pt-2">
                — Welcome. Tread gently. You are with family now.
              </p>

              {/* stat / signature row */}
              <div className="grid grid-cols-3 gap-4 border-t border-[#D4AF37]/15 pt-8 mt-10">
                <div>
                  <div className="font-serif text-2xl md:text-3xl text-[#D4AF37] italic">Wayanad</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#8E9A95] mt-1">Where they met</div>
                </div>
                <div>
                  <div className="numeral text-3xl text-[#D4AF37] flex items-center gap-2">
                    <Heart size={20} className="opacity-80" />
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#8E9A95] mt-1">Married & inseparable</div>
                </div>
                <div>
                  <div className="numeral text-3xl text-[#D4AF37] flex items-center gap-2">
                    01 <Leaf size={14} className="opacity-70" />
                  </div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#8E9A95] mt-1">Resort they built</div>
                </div>
              </div>

              {/* signature */}
              <div className="flex items-center gap-4 pt-8">
                <Heart size={14} className="text-[#D4AF37]" />
                <div>
                  <div className="font-serif italic text-xl text-[#E8E3D9]">Dilip & Vaishnavi</div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#8E9A95] mt-1">Founders · keepers of the forest</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FoundersSection;
